import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Card } from '@crave/ui';
import { api } from '../api.ts';
import { Notice, Section, errorText, when } from '../components/ui.tsx';

/** One background task that did not go through, as the API words it. No ids of people or Stripe. */
export interface OutboxItem {
  id: string;
  status: 'failed' | 'dead';
  task: string;
  problem: string;
  tries: number;
  happened_at: string;
  next_try_at: string | null;
  order_id: string | null;
  order_number: string | null;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const CSS = `
.ob-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }
.ob-item { display: grid; gap: 6px; min-width: 0; }
.ob-top { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 4px 12px; }
.ob-task { font-weight: 700; overflow-wrap: anywhere; }
.ob-badge { font-size: 13px; font-weight: 600; padding: 2px 10px; border-radius: 999px; border: 1px solid currentColor; white-space: nowrap; }
.ob-problem { margin: 0; overflow-wrap: anywhere; }
.ob-meta { font-size: 13px; color: var(--ink-soft); display: flex; flex-wrap: wrap; gap: 2px 12px; }
.ob-meta a { color: var(--ink); font-weight: 600; min-height: 44px; display: inline-flex; align-items: center; }
`;

/**
 * Settings: Outbox. Emails to customers and the Stripe clean-up after an erase run in the
 * background. The ones that did not go through are listed here in plain words, with one button to
 * try them all again now.
 */
export function Outbox() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const q = useQuery({
    queryKey: ['outbox'],
    queryFn: async () =>
      (await api.get<{ items: OutboxItem[] }>('/admin/reports/outbox')).data.items,
    refetchInterval: 30_000,
  });
  const retry = useMutation({
    mutationFn: async () =>
      (await api.post<{ ok: boolean; retried: number }>('/admin/reports/outbox/retry', {})).data,
    onSuccess: (r) => {
      setMsg({
        kind: 'ok',
        text:
          r.retried > 0
            ? `Trying ${plural(r.retried, 'task', 'tasks')} again now. This list updates in a minute.`
            : 'Nothing was waiting. Everything listed is already being tried.',
      });
      void qc.invalidateQueries({ queryKey: ['outbox'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => setMsg({ kind: 'error', text: errorText(e, 'Could not try again.') }),
  });

  const items = q.data ?? [];
  return (
    <Section
      title="Outbox"
      right={
        items.length > 0 ? (
          <Button onClick={() => retry.mutate()} disabled={retry.isPending}>
            {retry.isPending ? 'Trying again…' : 'Retry'}
          </Button>
        ) : undefined
      }
    >
      <style>{CSS}</style>
      <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
        Crave does some work in the background: emails to customers, and deleting an erased
        customer’s record at Stripe. Tasks that did not go through are listed here. Most are tried
        again automatically. Retry tries all of them right away.
      </p>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      {q.isError ? (
        <Notice kind="error">{errorText(q.error, 'Could not load the Outbox.')}</Notice>
      ) : !q.data ? (
        <p>Loading…</p>
      ) : items.length === 0 ? (
        <Card>
          <p style={{ margin: 0 }}>Everything went through. Nothing is waiting.</p>
        </Card>
      ) : (
        <ul className="ob-list">
          {items.map((i) => (
            <li key={i.id}>
              <Card className="ob-item">
                <div className="ob-top">
                  <span className="ob-task">{i.task}</span>
                  <span
                    className="ob-badge"
                    style={{ color: i.status === 'dead' ? 'var(--crave-red)' : 'var(--ink-soft)' }}
                  >
                    {i.status === 'dead' ? 'Stopped' : 'Trying again'}
                  </span>
                </div>
                <p className="ob-problem">{i.problem}</p>
                <div className="ob-meta">
                  <span>Started {when(i.happened_at, 'Never')}</span>
                  <span>{plural(i.tries, 'try', 'tries')} so far</span>
                  {i.next_try_at && <span>Next try {when(i.next_try_at, 'Never')}</span>}
                  {i.order_id && i.order_number && (
                    <Link to={`/orders/${i.order_id}`}>Order {i.order_number}</Link>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
